import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Task } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_TASKS_QUERY_KEY } from './tasks-query-keys';
import { addOrUpdateTaskCache } from './tasks-cache';

const PAGE_SIZE = 25;

/**
 * The filters a tasks list is read under. It is the second half of the query
 * key, so `addOrUpdateAllTasksCache` reads it back to decide which cached
 * lists a changed task still belongs to.
 */
export type FetchTasksQuery = {
    limit?: number;
    offset?: number;
    companyId?: string;
    teamIds?: string[];
    userIds?: string[];
    deviceIds?: string[];
    status?: 'ACTIVE' | 'COMPLETED' | 'ALERTED';
    liveOnly?: boolean;
    from?: string;
    to?: string;
    // When set, the passed userIds are companyUserIds.
    useNewUserSystem?: boolean;
};

export type FetchTasksResult = {
    tasks: Task[];
    status: 'error' | 'success' | 'pending';
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    refetch: () => void;
};

export function useFetchTasks(query: FetchTasksQuery): FetchTasksResult {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });
    const queryClient = useQueryClient();

    const {
        data,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: [FETCH_TASKS_QUERY_KEY, query],
        queryFn: ({ pageParam }) => {
            const limit = PAGE_SIZE;
            const offset = pageParam * limit;
            return openApiRef.current.tasks
                .getTasks({ ...query, limit, offset })
                .then((response) => {
                    return response;
                })
                .then((res) => {
                    // A page is a Map so the cache writes can replace one task
                    // in place without walking the list.
                    const taskMap = new Map<string, Task>();
                    res.data.data.tasks.data.forEach((task) => {
                        taskMap.set(task.id, task);
                        // Seed the per-task cache this read already paid for,
                        // so opening one task costs no second request.
                        addOrUpdateTaskCache(queryClient, task);
                    });
                    return taskMap;
                });
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.size > 0 ? allPages.length : undefined;
        }
    });

    const tasks = useMemo(() => {
        return data?.pages.flatMap((taskMap) => [...taskMap.values()]) ?? [];
    }, [data]);

    return {
        tasks,
        status,
        fetchNextPage: () => {
            void fetchNextPage();
        },
        isFetchingNextPage,
        hasNextPage,
        refetch: () => {
            void refetch();
        }
    };
}
